import React, { forwardRef, ReactNode } from 'react';
import { GetRef, InputNumberProps, InputNumber as NumberInput } from 'antd';
import Label from './Label';
import HelperText from './HelperText';
import cn from 'classnames';
import { CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { valueType } from 'antd/es/statistic/utils';
import './components.scss';

type InputNumberRef = GetRef<typeof NumberInput>;

type Props = {
  id: string;
  value: valueType | null | undefined;
  onChange: InputNumberProps['onChange'];
  min?: number;
  max?: number;
  statusIcon?: boolean;
  CustomIcon?: ReactNode;
  status?: 'success' | 'error';
  label?: string;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  labelHelperText?: string;
  labelClassName?: string;
  classNameContainer?: string;
  optional?: boolean;
} & Omit<React.ComponentProps<typeof NumberInput>, 'status'>;

const InputNumber = forwardRef<InputNumberRef, Props>(
  (
    {
      id,
      value,
      onChange,
      min,
      max,
      statusIcon,
      CustomIcon,
      status,
      label,
      labelClassName,
      classNameContainer,
      className,
      helperText,
      disabled,
      labelHelperText,
      required,
      optional,
      ...props
    }: Props,
    ref
  ) => {
    const statusIcons = React.useMemo(
      () => ({
        error: (
          <InfoCircleOutlined
            className={cn('text-red-500', {
              'text-light-text-secondary dark:text-dark-text-secondary':
                disabled,
            })}
          />
        ),
        success: (
          <CheckCircleOutlined
            className={cn('text-green-500', {
              'text-light-text-secondary dark:text-dark-text-secondary':
                disabled,
            })}
          />
        ),
      }),
      [disabled]
    );

    return (
      <div className={cn('flex flex-col', classNameContainer)}>
        {label && (
          <Label
            labelHelperText={labelHelperText}
            required={required}
            htmlFor={id}
            text={label}
            optional={optional}
            className={labelClassName}
          />
        )}
        <NumberInput
          id={id}
          value={value}
          onChange={onChange}
          min={min}
          disabled={disabled}
          max={max}
          suffix={statusIcon && status && statusIcons[status]}
          prefix={CustomIcon}
          controls={!disabled}
          ref={ref}
          onKeyDown={(e) => {
            if (
              min !== undefined &&
              min >= 0 &&
              (e.key === '-' || e.key === 'Subtract' || e.key === 'e' || e.key === 'E')
            ) {
              e.preventDefault();
            }
            props.onKeyDown?.(e);
          }}
          {...props}
          type="number"
          className={cn(
            '!h-[50px] !rounded-[12px] !border !border-[#E7E7E9] dark:!border-gray-700 bg-white dark:!bg-transparent !py-[12px] !pl-[20px] !pr-[16px] !gap-[10px] !shadow-none w-full flex items-center transition-all',
            '[&_.ant-input-number-input-wrap]:!w-full [&_.ant-input-number-input]:!p-0 [&_.ant-input-number-input]:!h-full [&_.ant-input-number-input]:!text-sm [&_.ant-input-number-input::placeholder]:!text-[#ACACAC] [&_.ant-input-number-handler-wrap]:!bg-transparent',
            'hover:!border-pink-500 focus-within:!border-pink-500',
            className,
            {
              '!border-red-500 ': status === 'error',
              '!border-green-500 ': status === 'success',
              'border-0 dark:border dark:!border-dark-text-placeholder !bg-light-button-label-primary dark:!bg-transparent':
                disabled,
            }
          )}
        />
        {helperText && (
          <HelperText text={helperText} disabled={disabled} status={status} />
        )}
      </div>
    );
  }
);

InputNumber.displayName = 'InputNumber';

export default InputNumber;
